/**
 * assets/myaiga-cart.js
 * ---------------------------------------------------------------------------
 * myAiga✓ — Cart integration.
 *
 * Responsibilities:
 *  1. Keep window.myAigaCart.attributes in sync with the live Shopify cart,
 *     so the `cartAttributes` adapter in myaiga-core.js always has fresh
 *     data (req #14, #16).
 *  2. Push the resolved membership status onto cart attributes
 *     (myaiga_status, myaiga_member, myaiga_source, myaiga_version) so a
 *     Shopify Function, discount, or Checkout UI Extension can read it at
 *     checkout time (req #17). This NEVER claims a discount was applied —
 *     it only exposes state for those systems to act on.
 *  3. Provide window.myAigaCartApi.addToCart/updateCart wrappers that any
 *     custom myAiga UI can call, on top of Shopify's standard /cart/*.js
 *     endpoints (no third-party app dependency).
 *  4. Detect cart changes made by the theme's OWN add/update/remove calls
 *     (which use plain fetch to /cart/add.js, /cart/update.js, etc. with no
 *     custom events) via a narrow, additive fetch tap — never blocking or
 *     altering the original request/response — and re-resolve membership +
 *     re-fetch cart totals so the banner/savings stay correct without a
 *     full page reload (req #15). No polling is used.
 * ---------------------------------------------------------------------------
 */
(function (window, document) {
  "use strict";

  var cfg = window.myAigaConfig || {};
  if (!cfg.enabled || !cfg.features || !cfg.features.cartMembershipEnabled) return;

  var VERSION = "1.0";
  var syncing = false; // guards against our own attribute-sync fetch retriggering itself

  function log() {
    if (cfg.debugMode && window.console) {
      console.log.apply(console, ["[myAiga:cart]"].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("cart.js request failed: " + res.status);
        return res.json();
      })
      .catch(function (err) {
        log("fetchCart failed (non-fatal):", err.message);
        return null;
      });
  }

  function setCartState(cart) {
    if (!cart) return;
    window.myAigaCart = window.myAigaCart || {};
    window.myAigaCart.attributes = cart.attributes || {};
    window.myAigaCart.itemCount = cart.item_count;
    window.myAigaCart.totalPrice = cart.total_price;
    window.myAigaCart.items = cart.items || [];
  }

  // ---------------------------------------------------------------------
  // Service fee product waiver (Theme Settings → myAiga✓ → Handling fee →
  // Fee enforcement → "Theme — auto-remove a Service Fee product for
  // members"). Only does anything when that specific enforcement mode is
  // selected AND a Service fee product is configured — otherwise a no-op.
  // This is real cart enforcement (removes the line item via the standard
  // cart API), not just display copy, but only works when the fee is
  // modeled as an actual product in the cart.
  // ---------------------------------------------------------------------
  var feeWaiverPromise = null;
  function waiveServiceFeeIfMember() {
    if (feeWaiverPromise) return feeWaiverPromise; // already in flight — wait on the same one

    var fee = cfg.handlingFee || {};
    if (fee.source !== "theme_product_removal" || !fee.serviceFeeVariantId) return Promise.resolve();
    if (!window.myAiga) return Promise.resolve();

    var isMember = window.myAiga.getStatus() === "member";
    var items = (window.myAigaCart && window.myAigaCart.items) || [];
    var feeLine = items.filter(function (item) {
      return String(item.variant_id) === fee.serviceFeeVariantId;
    })[0];

    if (isMember && feeLine) {
      feeWaiverPromise = fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: fee.serviceFeeVariantId, quantity: 0 }),
      })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (cart) {
          setCartState(cart);
          log("waived service fee product for confirmed member");
        })
        .catch(function (err) {
          log("service fee waiver failed (non-fatal):", err.message);
        })
        .finally(function () {
          feeWaiverPromise = null;
        });
      return feeWaiverPromise;
    } else if (!isMember && !feeLine && fee.serviceFeeAutoReadd) {
      feeWaiverPromise = fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: fee.serviceFeeVariantId, quantity: 1 }),
      })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function () {
          return fetchCart();
        })
        .then(function (cart) {
          setCartState(cart);
          log("re-added service fee product — membership no longer active");
        })
        .catch(function (err) {
          log("service fee re-add failed (non-fatal):", err.message);
        })
        .finally(function () {
          feeWaiverPromise = null;
        });
      return feeWaiverPromise;
    }
    return Promise.resolve();
  }

  // ---------------------------------------------------------------------
  // Checkout discount code (Theme Settings → myAiga✓ → Checkout discount
  // code). Uses Shopify's native checkout URL parameter (?discount=CODE)
  // to make checkout actually apply a real discount matching the
  // displayed member price — the one piece theme Liquid genuinely cannot
  // do on its own (req #17/#41). Finds the standard Shopify "checkout"
  // submit control(s) on the page (cart page and cart drawer both use the
  // same name="checkout" convention) and, when eligible, sends the
  // customer to /checkout?discount=CODE instead of the default form
  // submission — the cart contents are unaffected either way, since
  // /checkout reflects the current cart session regardless of how it's
  // reached.
  // ---------------------------------------------------------------------
  function cartContainsPlanProduct() {
    var d = cfg.checkoutDiscount || {};
    var planIds = d.planVariantIds || [];
    if (!planIds.length) return false;
    var items = (window.myAigaCart && window.myAigaCart.items) || [];
    return items.some(function (item) {
      return planIds.indexOf(String(item.variant_id)) !== -1;
    });
  }

  // Picks the discount code matching whichever plan is actually in the
  // cart right now — Monthly and Yearly can have different codes (e.g.
  // different %, different Shopify discount configuration). Falls back to
  // the single generic code for stores with only one plan / one code.
  function resolveDiscountCodeForCart() {
    var d = cfg.checkoutDiscount || {};
    var items = (window.myAigaCart && window.myAigaCart.items) || [];
    var byVariant = d.planDiscountCodes || {};
    for (var i = 0; i < items.length; i++) {
      var code = byVariant[String(items[i].variant_id)];
      if (code) return code;
    }
    return d.code || "";
  }

  function isEligibleForCheckoutDiscount() {
    var d = cfg.checkoutDiscount || {};
    if (!d.enabled || !resolveDiscountCodeForCart()) return false;
    if (d.excludePlans && cartContainsPlanProduct()) {
      log("checkout discount skipped — cart contains a membership plan product");
      return false;
    }
    if (d.applyTo === "everyone") return true;
    return !!window.myAiga && window.myAiga.getStatus() === "member";
  }

  function shouldInterceptCheckout() {
    var d = cfg.checkoutDiscount || {};
    var fee = cfg.handlingFee || {};
    // Intercept whenever there's real async cart state that could still be
    // in flight — either the discount mechanism or the fee waiver. If
    // neither is configured, there's nothing to wait for and the button
    // is left completely alone (native Shopify behavior).
    return !!d.enabled || fee.source === "theme_product_removal";
  }

  function bindCheckoutButtons() {
    if (!shouldInterceptCheckout()) return;
    var buttons = document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]'
    );
    buttons.forEach(function (btn) {
      if (btn.dataset.myaigaCheckoutBound) return;
      btn.dataset.myaigaCheckoutBound = "true";
      btn.addEventListener("click", function (event) {
        // Previously this only intercepted when a discount code redirect
        // was needed, and even then only prevented default AFTER deciding
        // eligibility from whatever state happened to be resolved at that
        // exact instant. If someone clicked Checkout quickly after picking
        // a plan — before the (async, fire-and-forget) Service Fee removal
        // and discount application finished — they'd sail through to
        // checkout with the old, unmodified cart: full price, fee still
        // present. Now this ALWAYS pauses navigation just long enough to
        // let those in-flight operations actually finish first.
        event.preventDefault();
        if (btn.dataset.myaigaCheckoutPending === "true") return; // ignore a double-click
        btn.dataset.myaigaCheckoutPending = "true";
        btn.disabled = true;

        waiveServiceFeeIfMember()
          .catch(function () { /* proceed to checkout regardless — non-fatal */ })
          .then(function () {
            var code = isEligibleForCheckoutDiscount() ? resolveDiscountCodeForCart() : "";
            window.location.href = code ? "/checkout?discount=" + encodeURIComponent(code) : "/checkout";
          });
      });
    });
  }

  // ---------------------------------------------------------------------
  // Real auto-apply — Shopify's native /discount/{code} endpoint actually
  // attaches the discount to the cart/checkout SESSION (not just a URL
  // param on one link), so it shows in the real cart totals immediately
  // and persists into checkout no matter how the customer gets there
  // (the cart's own Checkout button, Buy it now, Shop Pay, PayPal
  // Express, etc). The `bindCheckoutButtons` mechanism above only ever
  // covered one specific click target — this covers all of them, because
  // the discount is genuinely part of the cart, not injected at the last
  // click. This is a full navigation (Shopify's own requirement for this
  // endpoint), so it's only triggered once per actual code change, never
  // repeatedly.
  var applyingDiscount = false;
  function maybeApplyMemberDiscount() {
    if (applyingDiscount) return;
    // Only ever trigger this full-navigation apply from the cart page
    // itself — this fires right after picking a plan (an already
    // cart-page interaction, so a brief redirect-and-return there is
    // expected), never as a surprise redirect while someone is just
    // browsing a product page as an already-tagged member. Checkout-time
    // application for those cases is still covered by the existing
    // checkout-button redirect below, which only fires when the customer
    // is already navigating to checkout anyway.
    if (window.location.pathname.indexOf("/cart") !== 0) return;
    if (!window.myAiga || window.myAiga.getStatus() !== "member") return;
    var d = cfg.checkoutDiscount || {};
    if (!d.enabled) return;
    if (d.excludePlans && cartContainsPlanProduct()) {
      log("auto-apply skipped — cart contains a membership plan product");
      return;
    }
    var code = resolveDiscountCodeForCart();
    if (!code) return;

    var current = (window.myAigaCart && window.myAigaCart.attributes) || {};
    if (current.myaiga_applied_discount_code === code) return; // already applied this session

    applyingDiscount = true;
    log("auto-applying discount code via /discount/ endpoint:", code);
    // Record BEFORE navigating away, so the redirect back to /cart doesn't
    // see a stale attribute and loop.
    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: Object.assign({}, current, { myaiga_applied_discount_code: code }),
      }),
    })
      .catch(function () {
        /* proceed with the redirect regardless — worst case we retry next load */
      })
      .then(function () {
        window.location.href = "/discount/" + encodeURIComponent(code) + "?redirect=/cart";
      });
  }

  // ---------------------------------------------------------------------
  // Attribute sync — exposes membership state to checkout-side systems.
  // Merges with existing attributes so we never clobber other apps' data.
  // ---------------------------------------------------------------------
  function syncMembershipAttributes() {
    if (!cfg.features.checkoutMembershipEnabled) return Promise.resolve();
    if (!window.myAiga) return Promise.resolve();

    var status = window.myAiga.getStatus();
    var source = window.myAiga.getSource ? window.myAiga.getSource() : null;
    var current = (window.myAigaCart && window.myAigaCart.attributes) || {};

    if (
      current.myaiga_status === status &&
      current.myaiga_member === String(status === "member") &&
      current.myaiga_version === VERSION
    ) {
      return Promise.resolve(); // already in sync, avoid a redundant write
    }

    syncing = true;
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: Object.assign({}, current, {
          myaiga_status: status,
          myaiga_member: String(status === "member"),
          myaiga_source: source || "",
          myaiga_version: VERSION,
        }),
      }),
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        setCartState(cart);
        log("synced cart attributes:", status, source);
      })
      .catch(function (err) {
        log("attribute sync failed (non-fatal):", err.message);
      })
      .finally(function () {
        syncing = false;
      });
  }

  // ---------------------------------------------------------------------
  // Public cart API
  // ---------------------------------------------------------------------
  window.myAigaCartApi = {
    addToCart: function (payload) {
      return fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      }).then(function (res) {
        if (!res.ok) throw new Error("add to cart failed: " + res.status);
        return res.json();
      });
    },
    updateCart: function (payload) {
      return fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      }).then(function (res) {
        if (!res.ok) throw new Error("update cart failed: " + res.status);
        return res.json();
      });
    },
    getState: function () {
      return window.myAigaCart || null;
    },
  };

  // ---------------------------------------------------------------------
  // Detect the theme's own cart mutations WITHOUT touching window.fetch.
  // Overriding a global like window.fetch sitewide risks interfering with
  // any other script that inspects or relies on it being the untouched
  // native function — including, plausibly, this theme's own license/
  // integrity checks, which is exactly the kind of thing many commercial
  // themes verify. A MutationObserver watches the actual cart DOM instead
  // — it only reacts to visible changes in cart-related markup, and never
  // touches anything outside myAiga's own code path.
  // ---------------------------------------------------------------------
  var observerDebounce = null;
  function scheduleCartUpdatedFromDom() {
    if (syncing) return;
    clearTimeout(observerDebounce);
    observerDebounce = setTimeout(function () {
      window.dispatchEvent(new CustomEvent(window.myAiga ? window.myAiga.EVENTS.CART_UPDATED : "myaiga:cart-updated"));
    }, 150);
  }

  function watchableCartRoots() {
    // Cart page, cart drawer/minicart, and the plan selector's own
    // section wrapper are the only places cart contents actually change
    // in the DOM. Watching these specifically (rather than all of
    // document.body) keeps this cheap and avoids reacting to unrelated
    // page churn (carousels, animations, etc).
    return document.querySelectorAll(
      '[data-myaiga-plan-selector], .cart-drawer, #cart-drawer, .mini-cart, #mini-cart, [data-cart-items], .cart__items, form[action^="/cart"]'
    );
  }

  if (!window.__myAigaCartObserverBound) {
    window.__myAigaCartObserverBound = true;
    var observer = new MutationObserver(function () {
      scheduleCartUpdatedFromDom();
    });
    document.addEventListener("DOMContentLoaded", function () {
      watchableCartRoots().forEach(function (root) {
        observer.observe(root, { childList: true, subtree: true });
      });
    });
    // Also watch anything myAiga itself swaps in later (e.g. a cart
    // drawer that opens after page load) by re-scanning periodically at
    // a very low frequency — cheap, and only a fallback.
    setInterval(function () {
      watchableCartRoots().forEach(function (root) {
        if (!root.dataset.myaigaObserved) {
          root.dataset.myaigaObserved = "true";
          observer.observe(root, { childList: true, subtree: true });
        }
      });
    }, 3000);
  }

  window.addEventListener("myaiga:cart-updated", function () {
    fetchCart().then(function (cart) {
      setCartState(cart);
      waiveServiceFeeIfMember();
      bindCheckoutButtons();
    });
  });

  window.addEventListener("myaiga:membership-updated", function () {
    syncMembershipAttributes();
    waiveServiceFeeIfMember();
    maybeApplyMemberDiscount();
  });

  document.addEventListener("DOMContentLoaded", function () {
    fetchCart().then(function (cart) {
      setCartState(cart);
      syncMembershipAttributes();
      waiveServiceFeeIfMember();
      bindCheckoutButtons();
    });
  });
})(window, document);
